import { useEffect } from "react";
import { useAppStore } from "@/store/utils/hooks";
import { MemCommonEditorStore } from "@/components/note/store/mem-common-editor-store";
import { NoteType } from "@/components/note/types";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent } from "@/domains/metrics/tracked-event";
import { EventContext } from "@/domains/metrics/context";

/**
 * Hook that shows ghost text from upcoming calendar events when a note is empty.
 */
export function useGhostTextForCalendarEvent(editorStore: MemCommonEditorStore | undefined) {
  const { store } = useAppStore();

  const isReady =
    editorStore && editorStore.editorContentRendered && editorStore.noteType === NoteType.Note;
  const clearGhostText =
    isReady &&
    (!editorStore.isNoteEmpty ||
      editorStore.isShowingDifferences ||
      !!editorStore.noteObservable?.suggestionData);
  const nextUpcomingEventWithNoNote = store.spaceAccountCalendarEvents.nextUpcomingEventWithNoNote;

  useEffect(() => {
    if (!isReady) return;

    if (clearGhostText || !nextUpcomingEventWithNoNote) {
      editorStore.clearGhostText();
      return;
    }

    const abortController = new AbortController();

    const showGhostText = async () => {
      const result = await nextUpcomingEventWithNoNote.getTemplateIdAndEncodedContentAsync();
      if (!result?.encodedContent || abortController.signal.aborted) return;

      editorStore.showGhostText({
        encodedContent: result.encodedContent,
        onAccepted: () => {
          store.notes.linkNoteToCalendarEvent({
            noteId: editorStore.id,
            calendarEventId: nextUpcomingEventWithNoNote.id,
          });
          trackEvent(TrackedEvent.NoteLinkToCalendarEvent, {
            note_id: editorStore.id,
            connected_calendar_event_id:
              nextUpcomingEventWithNoNote.associatedConnectedCalendarEventId,
            context: EventContext.EditorGhostText,
          });
        },
      });
    };

    void showGhostText();

    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearGhostText, editorStore, isReady, nextUpcomingEventWithNoNote]);
}
